import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  FileText,
  FolderOpen,
  LineChart,
  MessageSquare,
  PanelsTopLeft,
  Pause,
  Play,
  Sparkles,
} from "lucide-react";

const stages = [
  {
    title: "Start with your know-how.",
    detail: "Forms, PDFs, spreadsheets, and company knowledge.",
    status: "Understand the work",
    label: "Your processes",
    context: "THE WAY YOUR COMPANY WORKS",
    checks: ["Documents", "Forms & PDFs", "Spreadsheets"],
    icon: FolderOpen,
    sides: [
      {
        title: "Quoting sheet",
        subtitle: "Your existing spreadsheets",
        icon: FileText,
        lines: ["Products & pricing", "Calculations & approvals"],
      },
      {
        title: "Company forms",
        subtitle: "PDFs, documents & procedures",
        icon: FileText,
        lines: ["The information you collect", "The steps your team follows"],
      },
    ],
  },
  {
    title: "Built into your software.",
    detail: "Quoting, digital forms, and connected business data.",
    status: "Built around your processes",
    label: "Custom software",
    context: "YOUR COMPANY, CONNECTED",
    checks: ["Digital forms", "Connected data", "Automated steps"],
    icon: PanelsTopLeft,
    sides: [
      {
        title: "Custom quoting",
        subtitle: "A spreadsheet becomes a tool",
        icon: PanelsTopLeft,
        lines: [
          "Your pricing & business rules",
          "Consistent quoting workflows",
        ],
      },
      {
        title: "Performance view",
        subtitle: "Your business data, together",
        icon: LineChart,
        lines: ["Activity across the company", "Clear ownership & next steps"],
      },
    ],
  },
  {
    title: "Ask. Act. Save time.",
    detail: "AI grounded in your company’s knowledge.",
    status: "Built for your team & customers",
    label: "AI assistance",
    context: "YOUR KNOWLEDGE, PUT TO WORK",
    checks: ["Find answers", "Prepare work", "Move faster"],
    icon: Sparkles,
    sides: [
      {
        title: "Internal assistant",
        subtitle: "Answers from your company data",
        icon: MessageSquare,
        lines: [
          "Which quotes need a follow-up?",
          "Prepare a draft for my review.",
        ],
      },
      {
        title: "Customer assistant",
        subtitle: "A website connected to your data",
        icon: MessageSquare,
        lines: [
          "Help customers find answers",
          "Use current product information",
        ],
      },
    ],
  },
];
export default function OutcomeScene() {
  const root = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    typeof document === "undefined" || !document.hidden,
  );
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const active = visible && pageVisible && !reduced && !paused;
  const phase = reduced ? 2 : Math.floor(elapsed / 4) % 3;
  const stage = stages[phase];
  const StageIcon = stage.icon;
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => setVisible(entries[0].isIntersecting),
      { threshold: 0.08 },
    );
    observer.observe(root.current);
    const visibility = () => setPageVisible(!document.hidden);
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const motion = () => setReduced(media.matches);
    document.addEventListener("visibilitychange", visibility);
    media.addEventListener("change", motion);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", visibility);
      media.removeEventListener("change", motion);
    };
  }, []);
  useEffect(() => {
    if (!active) return;
    let last = performance.now();
    const timer = setInterval(() => {
      const now = performance.now();
      const delta = (now - last) / 1000;
      last = now;
      setElapsed((value) => value + delta);
    }, 100);
    return () => clearInterval(timer);
  }, [active]);
  return (
    <div
      ref={root}
      className={`kw-outcome kw-outcome--${phase}${active ? " is-moving" : ""}`}
      aria-label="Illustrative workflow animation"
      data-phase={phase}
    >
      <div className="kw-outcome-atmosphere" aria-hidden="true">
        <span className="kw-glow-red" />
        <span className="kw-glow-grey" />
        <span className="kw-glow-red-secondary" />
        <span className="kw-glow-grey-secondary" />
      </div>
      {stage.sides.map((side, index) => {
        const Icon = side.icon;
        return (
          <div
            key={`${phase}-${index}`}
            className={`kw-outcome-input kw-outcome-input--${index === 0 ? "left" : "right"}`}
            aria-hidden="true"
          >
            <div className="kw-scene-side-heading">
              <Icon size={21} />
              <span>
                {side.title}
                <small>{side.subtitle}</small>
              </span>
            </div>
            <div className="kw-scene-side-lines">
              {side.lines.map((line) => (
                <span key={line}>
                  {phase === 2 ? (
                    <MessageSquare size={11} />
                  ) : (
                    <Check size={11} />
                  )}
                  {line}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      <div className="kw-outcome-panel" aria-live="off">
        <div className="kw-outcome-panel-top">
          <span>{stage.context}</span>
          <span className="kw-outcome-status">
            <span />
            {stage.status}
          </span>
        </div>
        <div className="kw-outcome-result" key={phase}>
          <div className="kw-outcome-symbol" aria-hidden="true">
            <StageIcon size={31} strokeWidth={1.5} />
          </div>
          <h2>{stage.title}</h2>
          <p>{stage.detail}</p>
        </div>
        <div className="kw-outcome-checks">
          {stage.checks.map((label) => (
            <span key={label}>
              <i>
                <Check size={12} />
              </i>
              {label}
            </span>
          ))}
        </div>
      </div>
      <div className="kw-outcome-bottom">
        <div className="kw-outcome-stages" aria-hidden="true">
          {stages.map((item, index) => (
            <span
              key={item.label}
              className={index === phase ? "is-current" : ""}
            >
              {item.label}
              {index < 2 && <ArrowRight size={12} />}
            </span>
          ))}
        </div>
        <button
          type="button"
          className="kw-scene-pause"
          aria-label={paused ? "Play animation" : "Pause animation"}
          aria-pressed={paused}
          onClick={() => setPaused((value) => !value)}
        >
          {paused ? <Play size={13} /> : <Pause size={13} />}
        </button>
      </div>
      <span className="kw-outcome-disclosure">
        An illustrative process. No live company data or AI connection.
      </span>
    </div>
  );
}
